import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Heading, Modal } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyConvertWebApiHandler_multiToSingleFamily } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useGetListingDetails } from '../../../hooks/queries/properties';

import { getListingRoute } from '../../../constants/urls';
import { STEPPER, STEPS } from './constants';

import SpinnerContainer from '../../SpinnerContainer';
import ModalFooter from '../ModalFooter';
import Congratulations from './Congratulations';
import PropertyToConvert from './PropertyToConvert';
import { ConvertableItem, ConvertableOption } from './types';
import UnitNumber from './UnitNumber';

import type { ConvertableItemType, ConvertableOptionType } from './types';

type MultiToSingleFamilyConversionModalProps = {
  setShowMultiToSingleFamilyConversionModal: (show: boolean) => void;
};

const MultiToSingleFamilyConversionModal = ({
  setShowMultiToSingleFamilyConversionModal,
}: MultiToSingleFamilyConversionModalProps) => {
  const router = useRouter();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const { floorPlans = [] } = listingDetails ?? {};

  const [convertableList, setConvertableList] = useState<Array<ConvertableOptionType>>([]);
  const [selectedItem, setSelectedItem] = useState<ConvertableItemType>(ConvertableItem({}));
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');
  const [convertedListingAlias, setConvertedListingAlias] = useState('');
  const [step, setStep] = useState(STEPS.INTERMITTENT);

  const { mutate: convertMultiToSingleFamily, isPending: isSubmitting } =
    useRentalManagerUserPropertyConvertWebApiHandler_multiToSingleFamily({
      onSuccess: (response) => {
        const { listingAlias: newListingAlias } = response;
        setConvertedListingAlias(newListingAlias);
        setStep(STEPS.CONGRATULATE);
      },
      onError: (error: unknown) => {
        const message = error instanceof Error ? error.message : 'An error occurred';
        setSubmitErrorMessage(message);
      },
    });

  const hideCancelButton = convertableList.length === 1 || step === STEPS.INTERMITTENT || step === STEPS.CONGRATULATE;
  const isSubmitDisabled = !selectedItem.id || step === STEPS.INTERMITTENT;

  useEffect(() => {
    const tempConvertableList: Array<ConvertableOptionType> = [];
    floorPlans.forEach((floorplan) => {
      if (floorplan.units && floorplan.units.length) {
        floorplan.units.forEach((unit) => {
          const { numBathrooms, numBedrooms, squareFeet, name, lowPrice, highPrice } = floorplan;
          const finalSquareFeet = unit.squareFeet || squareFeet;
          const unitToConvert = {
            ...unit,
            baths: numBathrooms,
            beds: numBedrooms,
            squareFeet: finalSquareFeet,
            floorplanName: name,
            lowPrice,
            highPrice,
          };

          tempConvertableList.push(ConvertableOption(unitToConvert));
        });
      } else {
        if (tempConvertableList.length && !floorplan.name) return;

        tempConvertableList.push(
          ConvertableOption({
            ...floorplan,
            baths: floorplan.numBathrooms,
            beds: floorplan.numBedrooms,
          }),
        );
      }
    });

    setConvertableList(tempConvertableList);

    // if there is only one choice inside the conversion list,
    // then proceed to second step
    if (tempConvertableList.length === 1) {
      setSelectedItem(ConvertableItem(tempConvertableList[0].convertableItem));
      setStep(STEPS.UNIT_NUMBER);
    } else {
      setStep(STEPS.PROPERTY);
    }
  }, [floorPlans]);

  const handleOptionSelect = (newOption: ConvertableOptionType) => {
    setSelectedItem(ConvertableItem(newOption.convertableItem));
    setSubmitErrorMessage('');
  };

  const handleSubmit = () => {
    if (step === STEPS.PROPERTY) {
      setStep(STEPS.UNIT_NUMBER);
      return;
    }
    if (step === STEPS.CONGRATULATE) {
      router.push(getListingRoute({ listingAlias: convertedListingAlias }));
      return;
    }

    const { id: modelId, unitName: unit } = selectedItem;

    convertMultiToSingleFamily({
      getParams: {
        modelId,
        unit: unit || undefined,
      },
    });
  };

  const handleUnitNumberBlur = (newUnitNumber: string) => {
    if (newUnitNumber !== selectedItem.unitName) {
      setSelectedItem({
        ...selectedItem,
        unitName: newUnitNumber,
      });
      setSubmitErrorMessage('');
    }
  };

  const handleModalReset = () => {
    setSelectedItem(ConvertableItem({}));
    setStep(STEPS.PROPERTY);
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && setShowMultiToSingleFamilyConversionModal(false)}
      header={<Heading level={2}>Convert floor plan/unit to a new listing</Heading>}
      body={
        <div data-testid="MultiToSingleFamilyConversionModal">
          <Box css={{ flexGrow: 0, flexShrink: 0, flexBasis: '100%' }}>
            {step === STEPS.PROPERTY && (
              <PropertyToConvert
                convertableList={convertableList}
                handleOptionSelect={handleOptionSelect}
                selectedItem={selectedItem}
              />
            )}
            {step === STEPS.UNIT_NUMBER && (
              <UnitNumber
                handleUnitNumberBlur={handleUnitNumberBlur}
                selectedItem={selectedItem}
                submitErrorMessage={submitErrorMessage}
              />
            )}
            {step === STEPS.CONGRATULATE && (
              <Congratulations convertedListingAlias={convertedListingAlias} handleModalReset={handleModalReset} />
            )}
            {step === STEPS.INTERMITTENT && <SpinnerContainer />}
          </Box>
        </div>
      }
      footer={
        <ModalFooter
          onCancel={hideCancelButton ? null : () => setShowMultiToSingleFamilyConversionModal(false)}
          onSubmit={handleSubmit}
          submitText={STEPPER[step].submitText}
          cancelText={STEPPER[step].cancelText}
          isSubmitting={isSubmitting}
          isSubmitDisabled={isSubmitDisabled}
        />
      }
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default MultiToSingleFamilyConversionModal;
